'use client';

import { useState } from 'react';
import Link from 'next/link';
import PhoneVerify from '../join/PhoneVerify';

type Mode = 'find-id' | 'reset-password';

interface FindResult {
  loginId: string;
  role: 'buyer' | 'ref';
}

const ROLE_LABEL: Record<FindResult['role'], string> = { buyer: '구매회원', ref: '영업회원' };

export default function FindAccountPage() {
  const [mode, setMode] = useState<Mode>('find-id');
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loginId, setLoginId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [err, setErr] = useState('');
  const [results, setResults] = useState<FindResult[] | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetView = (nextMode?: Mode) => {
    if (nextMode) setMode(nextMode);
    setPhone(''); setPhoneVerified(false); setLoginId(''); setNewPassword('');
    setErr(''); setResults(null); setDone(false);
  };

  const handleFindId = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!phoneVerified) { setErr('휴대폰 인증을 먼저 완료해 주세요.'); return; }
    setSubmitting(true);
    const res = await fetch('/api/account/find-id', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    });
    const data = await res.json();
    setSubmitting(false);
    if (res.ok) setResults(data.results);
    else setErr(data.error ?? '조회에 실패했습니다.');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!phoneVerified) { setErr('휴대폰 인증을 먼저 완료해 주세요.'); return; }
    if (newPassword.length < 4) { setErr('비밀번호는 4자 이상이어야 합니다.'); return; }
    setSubmitting(true);
    const res = await fetch('/api/account/reset-password', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginId, phone, newPassword }),
    });
    const data = await res.json();
    setSubmitting(false);
    if (res.ok) setDone(true);
    else setErr(data.error ?? '비밀번호 재설정에 실패했습니다.');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-start justify-center p-5">
      <div className="bg-white rounded-xl p-8 shadow-md w-full max-w-sm">
        <h1 className="text-xl font-bold text-black mb-6">아이디 / 비밀번호 찾기</h1>

        <div className="flex gap-2 mb-6">
          <button type="button" onClick={() => resetView('find-id')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium ${mode === 'find-id' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>아이디 찾기</button>
          <button type="button" onClick={() => resetView('reset-password')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium ${mode === 'reset-password' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>비밀번호 재설정</button>
        </div>

        {done ? (
          <div className="text-center py-6">
            <p className="text-sm text-green-600 mb-4">비밀번호가 재설정되었습니다.</p>
            <Link href="/login" className="text-blue-600 text-sm">로그인하러 가기 →</Link>
          </div>
        ) : results !== null ? (
          <div className="py-4">
            {results.length === 0 ? (
              <p className="text-sm text-gray-500 text-center">해당 번호로 가입된 아이디가 없습니다.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-400">가입된 아이디</p>
                {results.map(r => (
                  <div key={`${r.role}-${r.loginId}`} className="border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-black">{r.loginId}</span>
                    <span className="text-xs text-gray-400">{ROLE_LABEL[r.role]}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-center mt-4">
              <Link href="/login" className="text-blue-600 text-sm">로그인하러 가기 →</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={mode === 'find-id' ? handleFindId : handleResetPassword} className="space-y-3">
            {mode === 'reset-password' && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">아이디</label>
                <input required value={loginId} onChange={e => setLoginId(e.target.value)}
                  placeholder="구매회원 아이디 또는 영업자 ID"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900" />
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">휴대폰번호</label>
              <input required inputMode="numeric" value={phone}
                onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 11)); setPhoneVerified(false); }}
                placeholder="01012345678"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 mb-2" />
              <PhoneVerify phone={phone} verified={phoneVerified} onVerified={() => setPhoneVerified(true)} />
            </div>
            {mode === 'reset-password' && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">새 비밀번호</label>
                <input required type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  placeholder="4자 이상"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900" />
              </div>
            )}
            {err && <p className="text-red-500 text-xs">{err}</p>}
            <button type="submit" disabled={submitting}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
              {submitting ? '처리 중...' : mode === 'find-id' ? '아이디 찾기' : '비밀번호 재설정'}
            </button>
          </form>
        )}

        <div className="text-center mt-6">
          <Link href="/" className="text-xs text-gray-400 hover:text-gray-600">← 메인으로</Link>
        </div>
      </div>
    </div>
  );
}
